import React, { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Popup } from '@/components/UI/Popup/Popup';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { ProlongAdResponse } from '@/api/types/ads.types';
import { PROLONG_AD } from '@/api/mutations/ads.mutations';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';

type Props = {
  dataTestId?: string;
  onSuccess?: () => void;
};

export type ProlongByBalancePopupRef = {
  toggle: () => void;
};

export const ProlongByBalancePopup = React.forwardRef<
  ProlongByBalancePopupRef,
  Props
>(({ dataTestId, onSuccess }, ref) => {
  const { t } = useTranslation();
  const [isPopupOpen, togglePopup] = useToggleOpenState();
  const { categoryDetails } = useCurrentCategoryDetails();
  const [isLoading, setIsLoading] = useState(false);

  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const [prolongAd] = useMutation<ProlongAdResponse>(PROLONG_AD, {
    onCompleted: data => {
      refetchAdDetails().then(() => {
        if (data.prolongAd.entity) {
          togglePopup(false);
          onSuccess?.();
          setIsLoading(false);
        }
      });
    },
  });

  const description = t('pages.ad.pay_by_balance_description', {
    categoryName: categoryDetails?.name,
  });

  useImperativeHandle(
    ref,
    () => ({
      toggle: togglePopup,
    }),
    []
  );

  function onConfirmClick() {
    if (adDetails) {
      setIsLoading(true);
      prolongAd({ variables: { adId: adDetails.id } });
    }
  }

  return (
    <Popup
      dataTestId={dataTestId ?? AdPageTestIds.popup.prolongByBalance}
      isOpen={isPopupOpen}
      close={togglePopup}
      title={t('pages.ad.pay_by_balance')}
      description={description}
      actions={{
        left: {
          isGray: true,
          disabled: isLoading,
          text: t('common.deny'),
          onClick: togglePopup,
        },
        right: {
          disabled: isLoading,
          text: t('common.confirm'),
          onClick: onConfirmClick,
        },
      }}
    />
  );
});

ProlongByBalancePopup.displayName = 'ProlongByBalancePopup';
